
import { IStateUserSession, IStateUserSessionManager } from '../interface/state';

import Dexie from 'dexie';

const TABLE_NAME = "IUserState";
const TABLE_OPTIONS = "id, sessionName, grouping, languageId, tourId"


export interface DexieDBMap {
    [Key: string]: string;
}

export interface DexieDBOptions {
    name?: string;
    indexedDB?: any;
    IDBKeyRange?: any;
    version?: number;
    mappings?: DexieDBMap;
}

class DexieDB extends Dexie {
    constructor (options: DexieDBOptions = {}) {

        if ( !options.IDBKeyRange ) {
            options.IDBKeyRange = IDBKeyRange;
        }

        if ( !options.indexedDB ) {
            options.indexedDB = indexedDB;
        }

        const name = options.name ?? "Workspace-DB";

        super(name, options);

        this.version(options.version ?? 1)
            .stores(options.mappings ?? {});
    }
}

function _db(): DexieDB {
    const db = new DexieDB({
        version: 1,
        mappings: {
            [TABLE_NAME]: TABLE_OPTIONS,
        },
        name: TABLE_NAME,
    });

    return db;
}

function _session(data: Partial<IStateUserSession>): IStateUserSession {
    let session: IStateUserSession = {
        id: -1,
        sessionName: '',
        grouping: '',
        languageId: -1,
        tourId: -1,

        setCurrent: function(this: IStateUserSession) {
            _activeUserSession = this;
        },
    };
    Object.assign(session, data);

    return session;
}

function _load(name: string): Promise<IStateUserSession> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db();

            await db.open();

            const table = db.table(TABLE_NAME);

            const resultTable: Partial<IStateUserSession> = await table.get(name);

            db.close();

            if ( resultTable != null ) {            
                const resultObj = _session(resultTable);
                resolve(resultObj)
            } else {
                reject('Unable to find session named: ' + name)
            }            
        } catch (error) {
            reject(error);
        }
    });
}

function _save(state: IStateUserSession) : Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db();

            await db.open();

            /* remove anything which we can't save */
            const saveData: Partial<IStateUserSession> = {...state};
            delete saveData.setCurrent;

            const table = db.table(TABLE_NAME);

            const result = await table.put(saveData);

            db.close();

            ( result === saveData.id ) ?
                resolve() :
                reject('Failed to save data into patch-storage table');
        } catch (error) {
            reject(error);
        }
    });
}

function _remove(state: IStateUserSession): Promise<void> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db();

            await db.open();

            const table = db.table(TABLE_NAME);

            await table.delete(state.id);

            db.close();

            resolve();
        } catch (error) {
            reject(error);
        }
    });
}

let _activeUserSession: IStateUserSession|null = null;

function _all(): Promise<Array<IStateUserSession>> {
    return new Promise(async function(resolve, reject) {
        try {
            const db = _db();
            await db.open();
            const table = db.table(TABLE_NAME);
            /* Get all data stored in IndexedDb */
            const resultTable: Array<Partial<IStateUserSession>> = await
                table.toArray();
            db.close();
            /* Map each row to a IStateUserSesssion object */
            const resultObjects = resultTable.map(item => _session(item))
            resolve(resultObjects);
        } catch (error) {
            reject(error);
        }
    });
}

async function _getCurrent(): Promise<IStateUserSession|null> {
    const allSessions: any = await _all();
    _activeUserSession = allSessions.length > 0 ? allSessions[allSessions.length-1] : null;
    return _activeUserSession;
}


function UserStateManager(): IStateUserSessionManager {
    const manager: IStateUserSessionManager = {
        get: (user: string) => _load(user),
        save: (user: IStateUserSession) => _save(user),
        remove: (user: IStateUserSession) => _remove(user),
        current: () => _getCurrent(),
        new: () => _session({ id: new Date().getTime(), }),
    }

    return manager;
}

export default UserStateManager;
